import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Image,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import {
  ArrowDownOutlined,
  CloseCircleFilled,
  DatabaseFilled,
  EyeInvisibleOutlined,
  FileOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore"
import { db } from "../../../firebase/config"
import { loadingSpinWithBg } from "../../../components/Elements/LoadingSpinner"
import dayjs from "dayjs"
import { useAuthContext } from "../../../hooks/useAuthContext"
import { useCollection } from "../../../hooks/useCollection"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { parse } from "js2xmlparser"
import Highlighter from "react-highlight-words"
import InvoicePDFPreview from "./Helper/InvoicePDFPreview"
import DeleteInvoice from "./Helper/DeleteInvoice"
import ReportPDFGenerator from "./Helper/ReportPDFGenerator"
import InvoiceOrders from "../Clients/Helper/InvoiceOrders"
import HandleDocumentPayment from "./Helper/HandleDocumentPayment"
import CreateInvoiceAsDrawer from "../../Invoices/Create/CreateInvoiceAsDrawer"

const { Link, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

export default function AccountingInvoices() {
  // get data for manufacturer list
  const { data: manufacturerList, loading: manufacturerLoading } = useCollection("manufacturers")
  const { data: users, loading: usersLoading } = useCollection("users", [
    "group",
    "array-contains",
    "sales",
  ])

  // user declaration
  const { user, accountant, admin } = useAuthContext()

  // Set default dates to the current month using dayjs
  const startOfMonth = dayjs().startOf("month")
  const endOfMonth = dayjs().endOf("month")
  const startTimestamp = Timestamp.fromDate(startOfMonth.toDate())
  const endTimestamp = Timestamp.fromDate(endOfMonth.toDate())

  // search range state (Firebase Timestamps)
  const [startDate, setStartDate] = useState(startTimestamp)
  const [endDate, setEndDate] = useState(endTimestamp)

  // editing state
  const [editingInvoiceRecord, setEditingInvoiceRecord] = useState(null)
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false)

  const handleEditInvoice = (record) => {
    setEditingInvoiceRecord(record)
    setIsEditDrawerVisible(true)
  }

  const handleCloseEditDrawer = (refreshNeeded = false) => {
    setIsEditDrawerVisible(false)
    setEditingInvoiceRecord(null)
    if (refreshNeeded) {
      getInvoices() // Re-fetch invoices to show changes
    }
  }

  // selected client for search
  const [searchClient, setSearchClient] = useState("all")

  // selected invoices export data (initial structure; will be updated on selection)
  const [selectedData, setSelectedData] = useState({
    FailaPase: {
      Pamats: "CraftikaCMS",
      DatuAvots: "frmCraftikaCMS_Dokumentu_Eksports",
      Versija: "1",
      xmlversion: "v2",
      AtlasesGatavosanasLaiks: new Date().toISOString(),
      AtlaseDatumsNo: startDate.toDate().toISOString(),
      AtlaseDatumsLidz: endDate.toDate().toISOString(),
      AtlaseDokTips: "-1",
    },
    Dokuments: [],
  })

  // all fetched invoices
  const [allInvoices, setAllInvoices] = useState([])

  // table loading
  const [tableLoading, setTableLoading] = useState(false)

  // clients list state
  const [clientList, setClientList] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)

  // global loading
  const [globalLoading, setGloabalLoading] = useState(false)

  // state that holds xlsx data
  const [xlsxData, setXlsxData] = useState([])

  // selects the user for search
  const [selectedUser, setSelectedUser] = useState(null)

  const [showDeleted, setShowDeleted] = useState(false)
  const [includeWinding, setIncludeWinding] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [searchedColumn, setSearchedColumn] = useState("")
  const searchInput = useRef(null)

  // NEW: state to hold the selected rows for export
  const [selectedRows, setSelectedRows] = useState([])
  const [currentSelectedRowKeys, setCurrentSelectedRowKeys] = useState([])

  const [selectedManufacturerId, setSelectedManufacturerId] = useState(null)

  // State to track filtered data for accurate totals
  const [filteredInvoices, setFilteredInvoices] = useState([])

  // handles the available client list
  useEffect(() => {
    setClientsLoading(true)
    const ref = collection(db, "clients")

    let q
    if (selectedUser && selectedUser.id) {
      q = query(ref, where("user", "==", selectedUser.id))
    } else {
      q = query(ref)
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let results = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))

      // ✅ Filter logic: hide certain clients if not admin/accountant
      if (!admin && !accountant) {
        results = results.filter((client) => client.user !== "E1XNaWvT6PccXRSKB3JNlNehI362")
      }

      setClientList(results)
      setClientsLoading(false)
    })

    return () => unsubscribe()
  }, [selectedUser, admin, accountant])

  // Initialize filtered invoices when allInvoices changes
  useEffect(() => {
    setFilteredInvoices(allInvoices)
  }, [allInvoices])

  // returns global loading indicator if needed
  const returnGlobalLoading = () => {
    return <>{loadingSpinWithBg(75, "Generating File...")}</>
  }

  if (globalLoading) {
    return returnGlobalLoading()
  }

  // function to download the XML file using js2xmlparser
  const downloadXml = async () => {
    setGloabalLoading(true)
    try {
      const sequenceRef = doc(db, "sequence", "invoiceXmlExports")
      const sequenceSnapshot = await getDoc(sequenceRef)
      const currentXml = sequenceSnapshot.data().lastXml
      let options = {
        declaration: { include: true, encoding: "UTF-8" },
        format: { doubleQuotes: true },
      }
      const xml = parse("Root", selectedData, options)
      const blob = new Blob([xml], { type: "application/xml" })
      saveAs(blob, "statement_" + (currentXml + 1))
      await updateDoc(sequenceRef, {
        lastXml: currentXml + 1,
        updated: serverTimestamp(),
        updatedBy: user.uid,
      })
      setGloabalLoading(false)
    } catch (error) {
      message.error(`Error: ${error.message}`)
      setGloabalLoading(false)
    }
  }

  // function to download the Excel file
  const downloadExcel = () => {
    let wb = XLSX.utils.book_new()
    let ws = XLSX.utils.json_to_sheet(xlsxData)
    XLSX.utils.book_append_sheet(wb, ws, "selection")
    XLSX.writeFile(wb, "selection.xlsx")
  }

  // formats a Firebase timestamp object to a string using dayjs
  const formatDate = (dateObject) => {
    return dayjs.unix(dateObject.seconds).format("DD/MM/YYYY HH:mm")
  }

  // Define row selection behavior.
  const rowSelection = {
    selectedRowKeys: currentSelectedRowKeys,

    onChange: (selectedRowKeysFromAntD, selectedRowsDataFromAntD) => {
      setSelectedRows(selectedRowsDataFromAntD)
      setCurrentSelectedRowKeys(selectedRowKeysFromAntD)
      // *** NEW: Logic to manage the selected manufacturer ID ***
      // Logic to manage the selected manufacturer ID
      if (selectedRowsDataFromAntD.length >= 1) {
        setSelectedManufacturerId(selectedRowsDataFromAntD[0].manufacturer?.id)
      } else {
        setSelectedManufacturerId(null)
      }

      const dokumentsArray = selectedRowsDataFromAntD.map((invoice) => {
        const productKontejums = invoice.prices.map((price) => {
          return {
            DKonts: price.dAccount.toString(),
            DNosaukums: price.dName,
            DTips: "0",
            DDaudzums: price.printedQuantity
              ? price.printedQuantity.toString()
              : (price.count / 1000).toFixed(2),
            DValuta: invoice.currency.toUpperCase(),
            KKonts: price.cAccount.toString(),
            KNosaukums: price.cName,
            KTips: "0",
            KDaudzums: price.printedQuantity
              ? price.printedQuantity.toString()
              : (price.count / 1000).toFixed(2),
            KValuta: invoice.currency.toUpperCase(),
            PreceID: "0",
            SummaBIL: parseFloat(price.sum).toFixed(2),
            Summa: parseFloat(price.sum).toFixed(2),
            Text: price.product,
            Tips: "1",
            Rezerve1: "0",
            Rezerve2: price.unit === "thous." ? "tūkstotis" : "gab.",
            Rezerve3: price.unit === "thous." ? "61111" : "61220",
            Cena: parseFloat(price.price).toFixed(2),
            PVN: "21",
            Partija: ".",
            AkcizeMarka: "",
          }
        })
        const taxBlock = {
          DKonts: "2310",
          DNosaukums: "Norēķini ar pircējiem un pasūtītājiem",
          DTips: "0",
          DDaudzums: "0.00",
          DValuta: invoice.currency.toUpperCase(),
          KKonts: "5721",
          KNosaukums: "PVN konts",
          KTips: "0",
          KDaudzums: "0.00",
          KValuta: invoice.currency.toUpperCase(),
          PreceID: "0",
          SummaBIL: parseFloat(invoice.totalData.totalVat).toFixed(2),
          Summa: parseFloat(invoice.totalData.totalVat).toFixed(2),
          Text: "21",
          Tips: "3",
          Rezerve1: "",
          Rezerve2: "",
          Rezerve3: "",
          Cena: "0.00",
          PVN: "21",
          Partija: ".",
          AkcizeMarka: "",
        }

        return {
          HronoID: invoice.hronodId,
          HronoKontets: 1,
          HronoTips: 8,
          HronoDatums: new Date(invoice.documentDate.seconds * 1000).toISOString(),
          HronoDocName: "Pavadzīme",
          HronoDocNr: invoice.docNumber,
          ValutasKods: invoice.currency.toUpperCase(),
          Pamatojums:
            invoice.prices && invoice.prices.length > 0
              ? invoice.prices[0].product
              : "Default description",
          DateTerm: invoice.paymentDueDate
            ? new Date(invoice.paymentDueDate.seconds * 1000).toISOString()
            : new Date().toISOString(),
          Fadr: invoice.manufacturer.deliveryAddress,
          Kadr:
            invoice.client.deliveryAddress +
            ", " +
            invoice.client.city +
            ", " +
            invoice.client.postalCode,
          GadaID: "50",
          Rezerve1: invoice.manufacturer.docPrefix === "AR" ? "ART" : "",
          Rezerve2: "ar pārskaitījumu",
          Rezerve3: "4",
          SagDatums: new Date(invoice.documentDate.seconds * 1000).toISOString(),
          ProjektsKods: "",
          PersonalName: invoice.client.client,
          PersonalVards: "",
          PersonalNmrNr: invoice.client.reg,
          PersonalPvnNr: invoice.client.vat,
          PersonalAdresscity: invoice.client.city,
          PersonalAdressstreet: invoice.client.deliveryAddress,
          PersonalPastaindekss: invoice.client.postalCode,
          PersonalValstsID: "428",
          PersonalTips: "0",
          PersonalKonts1: invoice.client.accountNumber,
          Kontejums: [...productKontejums, taxBlock],
          PZrekviziti: {
            TransportsPersonalID: "-1",
            Automarka:
              invoice.transport && invoice.transport.automarka ? invoice.transport.automarka : "",
            Soferis:
              invoice.transport && invoice.transport.soferis ? invoice.transport.soferis : "",
            Darijumaapraksts: "Preču piegāde",
            DRN: "",
            Iepakojums: "",
            ProjektsKods: "",
            AgentsID: "-1",
          },
        }
      })

      setSelectedData({
        FailaPase: {
          Pamats: "CraftikaCMS",
          DatuAvots: "CraftikaCMS_Dokumentu_Eksports",
          Versija: "1",
          xmlversion: "v2",
          AtlasesGatavosanasLaiks: new Date().toISOString(),
          AtlaseDatumsNo: startDate.toDate().toISOString(),
          AtlaseDatumsLidz: endDate.toDate().toISOString(),
          AtlaseDokTips: "-1",
        },
        Dokuments: dokumentsArray,
      })

      const excelArray = selectedRowsDataFromAntD.map((item) => ({
        invoiceDate: formatDate(item.documentDate),
        invoiceNumber: String(item.docNumber).padStart(4, "0"),
        invoicePrefix: item.docPrefix,
        currency: item.currency,
        totalNeto: item.totalData.totalNetoSum,
        totalVat: item.totalData.totalVat,
        totalSum: item.totalData.totalSum,
        manufacturer: item.manufacturer.company,
        manufacturerVat: item.manufacturer.vat,
        manufacturerBank: item.manufacturer.bankName,
        manufacturerBankAccount: item.manufacturer.accountNumber,
        manufacturerAddress: item.manufacturer.deliveryAddress,
        manufacturerCity: item.manufacturer.deliveryCity,
        manufacturerPostalCode: item.manufacturer.deliveryPC,
        client: item.client.client,
        clientBank: item.client.bankName,
        clientBankAccount: item.client.accountNumber,
        clientAddress: item.client.deliveryAddress,
        clientVat: item.client.vat,
      }))
      setXlsxData(excelArray)
    },
    // getCheckboxProps
    getCheckboxProps: (record) => ({
      disabled:
        selectedManufacturerId !== null && record.manufacturer?.id !== selectedManufacturerId,
    }),
  }

  // on change select for clients
  const clientSearchSelect = (value) => {
    setSearchClient(value)
  }
  // Handler to set the full user object.
  const handleUserSelect = (value) => {
    if (value === "all") {
      setSelectedUser(null)
    } else {
      const userObj = users.find((userItem) => userItem.id === value)
      setSelectedUser(userObj)
    }
  }

  // on datepicker change for export range using dayjs
  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      const [start, end] = dates
      setStartDate(Timestamp.fromDate(start.toDate()))
      setEndDate(Timestamp.fromDate(end.endOf("day").toDate()))
    }
  }

  // gets invoices based on search criteria
  const getInvoices = async () => {
    setTableLoading(true)
    try {
      const ref = collection(db, "invoices")
      let constraints = []

      if (startDate && endDate) {
        constraints.push(where("documentDate", ">=", startDate))
        constraints.push(where("documentDate", "<=", endDate))
      }
      if (searchClient !== "all") {
        constraints.push(where("client.id", "==", searchClient))
      }
      if (selectedUser && selectedUser !== "all") {
        constraints.push(where("userId", "==", selectedUser.id))
      }
      if (!showDeleted) {
        constraints.push(where("deleted", "==", false))
      }
      const q = query(ref, ...constraints)
      let results = []
      const querySnapshot = await getDocs(q)
      querySnapshot.forEach((doc) => {
        const invoice = { ...doc.data(), id: doc.id }

        // NEW: Conditionally exclude "WINDING SIA" based on the checkbox state
        if (!includeWinding && invoice.manufacturer?.company === "WINDING SIA") {
          return
        }

        results.push(invoice)
      })

      setAllInvoices(results)
      setTableLoading(false)
    } catch (error) {
      message.error(`Error: ${error.message}`)
      console.log(error.message)
      setGloabalLoading(false)
    }
  }

  // Calculate totals for the table footer
  const calculateTotals = (invoices) => {
    const totalNetoSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalNetoSum || 0),
      0,
    )
    const totalVat = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalVat || 0),
      0,
    )
    const totalSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalSum || 0),
      0,
    )
    return { totalNetoSum, totalVat, totalSum }
  }

  // Handle table changes (including filtering, sorting, pagination)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    // extra.currentDataSource contains the filtered data after all filters are applied
    if (extra && extra.currentDataSource) {
      setFilteredInvoices(extra.currentDataSource)
    } else {
      // Fallback to allInvoices if currentDataSource is not available
      setFilteredInvoices(allInvoices)
    }
  }

  let manufacturerFilter =
    manufacturerList &&
    manufacturerList.map((item) => ({
      text: item.company,
      value: item.company,
    }))

  const seriesFilters = [...new Set(allInvoices.map((invoice) => invoice.docPrefix))].map(
    (prefix) => ({
      text: prefix,
      value: prefix,
    }),
  )

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText("")
  }

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  })

  const defaultColumns = [
    {
      title: "Logo",
      dataIndex: "InvoiceLogo",
      width: "5%",
      align: "left",
      render: (value) => {
        if (value) {
          return <Image width={50} alt="invoice logo" preview={false} src={value} />
        } else {
          return <Text>CMS</Text>
        }
      },
    },
    {
      title: "Number",
      dataIndex: "docNumber",
      width: "10%",
      align: "left",
      sorter: (a, b) => a.docNumber - b.docNumber,
      sortDirections: ["descend", "ascend"],
      ...getColumnSearchProps("docNumber"),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value} - <CloseCircleFilled />
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Date",
      dataIndex: ["documentDate", "seconds"],
      width: "10%",
      align: "left",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {dayjs.unix(value).format("DD.MM.YYYY")}
          </Text>
        ) : (
          <Text>{dayjs.unix(value).format("DD.MM.YYYY")}</Text>
        ),
    },
    {
      title: "Series",
      dataIndex: "docPrefix",
      width: "7%",
      align: "left",
      filters: seriesFilters,
      onFilter: (value, record) => record.docPrefix === value,
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "15%",
      ellipsis: {
        showTitle: true,
      },
      sorter: (a, b) => a.client.client.localeCompare(b.client.client),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "left",
      ellipsis: {
        showTitle: true,
      },
      sorter: (a, b) => a.manufacturer.company.localeCompare(b.manufacturer.company),
      filters: manufacturerFilter,
      onFilter: (value, record) => record.manufacturer.company === value,
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Payment",
      dataIndex: "paymentStatus",
      width: "10%",
      hidden: !accountant,
      filters: [
        { text: "Paid", value: "paid" },
        { text: "Partial", value: "partial" },
        { text: "Unpaid", value: "unpaid" },
      ],
      onFilter: (value, record) => {
        if (value === "paid") return record.paymentStatus === 2
        if (value === "partial") return record.paymentStatus === 3
        return record.paymentStatus === 0 || record.paymentStatus === 1
      },
      align: "center",
      render: (value, record) => (
        <HandleDocumentPayment
          record={record}
          collectionName="invoices"
          onPaymentUpdate={getInvoices}
        />
      ),
    },
    {
      title: "Neto (eur)",
      dataIndex: ["totalData", "totalNetoSum"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Vat (eur)",
      dataIndex: ["totalData", "totalVat"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Total (eur)",
      dataIndex: ["totalData", "totalSum"],
      width: "5%",
      align: "right",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Remaining (eur)",
      key: "remaining",
      width: "6%",
      align: "right",
      render: (_, record) => {
        const total = record.totalData?.totalSum || 0
        const paid = record.paidAmount || 0
        const remaining = Math.max(0, total - paid)
        if (record.paymentStatus === 2) return <Text style={{ color: "#52c41a" }}>—</Text>
        if (record.paymentStatus === 3)
          return <Text style={{ color: "#fa8c16", fontWeight: 600 }}>{remaining.toFixed(2)}</Text>
        return <Text style={{ color: "#f5222d" }}>{total.toFixed(2)}</Text>
      },
    },
    {
      title: "File",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => (
        <Link href={value}>
          <ArrowDownOutlined />
        </Link>
      ),
    },
    {
      title: "Pay. Until",
      dataIndex: "payUntilFormatted",
      width: "10%",
      align: "center",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Orders",
      dataIndex: "orderReferenceId",
      width: "10%",
      align: "right",
      render: (value) => <InvoiceOrders orderReferenceIds={value} />,
    },
    {
      title: "Show",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => {
        if (value) {
          return <InvoicePDFPreview pdfUrl={value} />
        } else {
          return <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
        }
      },
    },
    {
      title: "Actions",
      width: "10%",
      align: "center",
      render: (value, record) =>
        record.deleted ? (
          <Button size="small" disabled>
            Deleted
          </Button>
        ) : (
          <Space>
            <Button size="small" type="primary" ghost onClick={() => handleEditInvoice(record)}>
              Edit
            </Button>
            <DeleteInvoice record={record} invoice={true} onDeleted={getInvoices} />
          </Space>
        ),
    },
  ]

  const onDeletedCheck = (e) => {
    setShowDeleted(e.target.checked)
  }

  return (
    <div style={{ padding: 15 }}>
      {isEditDrawerVisible && editingInvoiceRecord && (
        <CreateInvoiceAsDrawer
          key={editingInvoiceRecord.id}
          editingInvoiceData={editingInvoiceRecord}
          openDrawerInitially={true}
          onDrawerClose={handleCloseEditDrawer}
          forProforma={editingInvoiceRecord.docType === "proforma"}
        />
      )}
      <Row>
        <Col span={24}>
          <PageHeader text="All Invoices" icon={<DatabaseFilled />} />
        </Col>
        <Col span={24}>
          <Divider orientation="left">Selection</Divider>
          <Space>
            {admin && (
              <Checkbox
                onChange={(e) => setIncludeWinding(e.target.checked)}
                checked={includeWinding}>
                Include Winding
              </Checkbox>
            )}
            <Checkbox onChange={onDeletedCheck}>Deleted</Checkbox>
            <RangePicker
              format="DD.MM.YYYY"
              onChange={onDateChange}
              defaultValue={[startOfMonth, endOfMonth]}
            />
            <Select
              showSearch
              placeholder="All users selected"
              optionFilterProp="children"
              style={{ width: 250 }}
              defaultValue="all"
              loading={usersLoading}
              onChange={handleUserSelect}
              filterOption={(input, option) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }>
              <Option key="all" value="all">
                All Users
              </Option>
              {users &&
                users.map((userItem) => (
                  <Option key={userItem.id} value={userItem.id}>
                    {userItem.email}
                  </Option>
                ))}
            </Select>
            <Select
              showSearch
              placeholder="All clients selected"
              optionFilterProp="children"
              style={{ width: 250 }}
              defaultValue="all"
              loading={clientsLoading}
              onChange={clientSearchSelect}
              filterOption={(input, option) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }>
              <Option key={1} value="all">
                All Clients
              </Option>
              {clientList &&
                clientList.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.client}
                  </Option>
                ))}
            </Select>
            <Button onClick={getInvoices}>Search</Button>
          </Space>
        </Col>
        <Col span={24}>
          <Divider orientation="left">Exports</Divider>
          <Space>
            <Button onClick={downloadXml} disabled={selectedRows.length === 0}>
              <FileOutlined /> Export to XML
            </Button>
            <Button onClick={downloadExcel}>
              <FileOutlined /> Export to XLSX
            </Button>
            <ReportPDFGenerator
              reportData={allInvoices}
              reportTitle="Invoice Report"
              managerName={selectedUser ? selectedUser.email : "All Users"}
              dateRange={{
                from: startDate ? dayjs(startDate.toDate()).format("DD/MM/YYYY") : "",
                to: endDate ? dayjs(endDate.toDate()).format("DD/MM/YYYY") : "",
              }}
              totals={calculateTotals(allInvoices)}
            />
          </Space>
        </Col>
        <Col span={24}>
          <Table
            rowSelection={rowSelection}
            size="small"
            rowKey={(record) => record.id}
            bordered
            dataSource={allInvoices}
            loading={tableLoading || manufacturerLoading}
            columns={defaultColumns}
            onChange={handleTableChange}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            footer={() => {
              const { totalNetoSum, totalVat, totalSum } = calculateTotals(filteredInvoices)
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Space>
                    <Tag color="blue">Total Neto (eur): {totalNetoSum.toFixed(2)}</Tag>
                    <Tag color="blue">Total Vat (eur): {totalVat.toFixed(2)}</Tag>
                    <Tag color="blue">Total (eur): {totalSum.toFixed(2)}</Tag>
                  </Space>
                </div>
              )
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
